const TARGET_SAMPLE_RATE = 16_000;

export function isWavFile(file: File): boolean {
  return (
    file.type === 'audio/wav' ||
    file.type === 'audio/x-wav' ||
    file.name.toLowerCase().endsWith('.wav')
  );
}

/**
 * Convert any browser-decodable audio/video file to a 16 kHz mono WAV.
 * Tries the native Web Audio API first (fast, zero download).
 * Falls back to FFmpeg.wasm for large files or unsupported codecs.
 */
export async function convertToWav(
  file: File,
  onPhase?: (phase: string) => void,
): Promise<File> {
  try {
    return await convertWithWebAudio(file, onPhase);
  } catch (err) {
    console.warn('Web Audio API fehlgeschlagen, verwende FFmpeg.wasm:', err);
  }
  return await convertWithFFmpeg(file, onPhase);
}

// ---------------------------------------------------------------------------
// Strategy 1 – Web Audio API (fast, no download, works for files < ~500 MB)
// ---------------------------------------------------------------------------

async function convertWithWebAudio(
  file: File,
  onPhase?: (phase: string) => void,
): Promise<File> {
  onPhase?.('Datei wird gelesen…');
  const arrayBuffer = await file.arrayBuffer();

  onPhase?.('Audio wird dekodiert…');
  const audioCtx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });
  let decoded: AudioBuffer;
  try {
    decoded = await audioCtx.decodeAudioData(arrayBuffer);
  } finally {
    await audioCtx.close();
  }

  onPhase?.('WAV wird erzeugt…');
  const mono = mixToMono(decoded);
  const wavBlob = encodeWav(mono, TARGET_SAMPLE_RATE);

  const baseName = file.name.replace(/\.[^.]+$/, '');
  return new File([wavBlob], `${baseName}.wav`, { type: 'audio/wav' });
}

// ---------------------------------------------------------------------------
// Strategy 2 – FFmpeg.wasm (handles large files & exotic codecs)
// Uses WORKERFS mount to avoid copying the entire file into WASM memory.
// ---------------------------------------------------------------------------

const WRITE_FILE_MAX = 1.5 * 1024 * 1024 * 1024; // 1.5 GB

async function convertWithFFmpeg(
  file: File,
  onPhase?: (phase: string) => void,
): Promise<File> {
  onPhase?.('FFmpeg wird geladen (einmalig ~30 MB)…');

  const { FFmpeg } = await import('@ffmpeg/ffmpeg');
  const { toBlobURL } = await import('@ffmpeg/util');

  const ffmpeg = new FFmpeg();
  ffmpeg.on('log', ({ message }) => console.log('[FFmpeg]', message));
  ffmpeg.on('progress', ({ progress }) => {
    const pct = Math.max(0, Math.min(100, Math.round(progress * 100)));
    onPhase?.(`Audio wird extrahiert… ${pct}%`);
  });

  const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
  await ffmpeg.load({
    coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
    wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
  });

  let inputPath = 'input';
  let mounted = false;

  // WORKERFS mounts the File object directly – no memory copy needed.
  try {
    onPhase?.('Datei wird eingebunden…');
    await ffmpeg.createDir('/work');
    await ffmpeg.mount('WORKERFS' as any, { files: [file] }, '/work');
    inputPath = `/work/${file.name}`;
    mounted = true;
  } catch (mountErr) {
    console.warn('WORKERFS mount fehlgeschlagen:', mountErr);
  }

  if (!mounted) {
    if (file.size > WRITE_FILE_MAX) {
      await ffmpeg.terminate();
      throw new Error('TOO_LARGE');
    }
    onPhase?.('Datei wird in den Speicher gelesen…');
    const { fetchFile } = await import('@ffmpeg/util');
    await ffmpeg.writeFile('input', await fetchFile(file));
  }

  onPhase?.('Audio wird extrahiert…');
  await ffmpeg.exec([
    '-i', inputPath,
    '-vn',
    '-ar', String(TARGET_SAMPLE_RATE),
    '-ac', '1',
    '-c:a', 'pcm_s16le',
    '-f', 'wav',
    'output.wav',
  ]);

  const outputData = await ffmpeg.readFile('output.wav');

  if (mounted) {
    try { await ffmpeg.unmount('/work'); } catch { /* ok */ }
  } else {
    try { await ffmpeg.deleteFile('input'); } catch { /* ok */ }
  }
  try { await ffmpeg.deleteFile('output.wav'); } catch { /* ok */ }
  await ffmpeg.terminate();

  const baseName = file.name.replace(/\.[^.]+$/, '');
  return new File([outputData], `${baseName}.wav`, { type: 'audio/wav' });
}

// ---------------------------------------------------------------------------
// WAV encoding helpers
// ---------------------------------------------------------------------------

function mixToMono(buffer: AudioBuffer): Float32Array {
  const length = buffer.length;
  const channels = buffer.numberOfChannels;

  if (channels === 1) return buffer.getChannelData(0);

  const mono = new Float32Array(length);
  for (let ch = 0; ch < channels; ch++) {
    const data = buffer.getChannelData(ch);
    for (let i = 0; i < length; i++) {
      mono[i] += data[i];
    }
  }

  const scale = 1 / channels;
  for (let i = 0; i < length; i++) {
    mono[i] *= scale;
  }
  return mono;
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const numSamples = samples.length;
  const bytesPerSample = 2;
  const dataSize = numSamples * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');

  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);

  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < numSamples; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7FFF, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

function writeString(view: DataView, offset: number, str: string): void {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}
